import { AxiosError, isAxiosError } from 'axios';

/**
 * API 에러 응답 형식 (백엔드 표준)
 */
export interface ApiErrorResponse {
  success: false;
  data: null;
  message: string;
  timestamp: string;
  statusCode: number;
  error?: {
    code: string;
    message: string;
    path?: string;
    details?: Record<string, unknown>;
  };
}

/**
 * 에러 타입 분류
 */
export type ErrorType =
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'BAD_REQUEST'
  | 'VALIDATION_ERROR'
  | 'TOO_MANY_REQUESTS'
  | 'SERVER_ERROR'
  | 'UNKNOWN';

/**
 * 업계 표준 API 에러 클래스
 * - Axios Interceptor에서 생성
 * - React Query에서 에러 타입으로 사용
 * - 컴포넌트에서 instanceof 체크 가능
 */
export class ApiError extends Error {
  public readonly type: ErrorType;
  public readonly statusCode: number;
  public readonly userMessage: string;
  public readonly backendCode?: string;
  public readonly details?: Record<string, unknown>;
  public readonly isRetryable: boolean;

  constructor(params: {
    type: ErrorType;
    statusCode: number;
    message: string;
    userMessage: string;
    backendCode?: string;
    details?: Record<string, unknown>;
  }) {
    super(params.message);
    this.name = 'ApiError';
    this.type = params.type;
    this.statusCode = params.statusCode;
    this.userMessage = params.userMessage;
    this.backendCode = params.backendCode;
    this.details = params.details;
    this.isRetryable = this.checkRetryable();

    // Error 클래스 상속 시 프로토타입 체인 유지
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  private checkRetryable(): boolean {
    // 네트워크 에러, 타임아웃, 5xx 에러는 재시도 가능
    return (
      this.type === 'NETWORK_ERROR' ||
      this.type === 'TIMEOUT' ||
      this.statusCode >= 500
    );
  }

  /**
   * 네트워크 관련 에러인지 확인
   */
  get isNetworkError(): boolean {
    return this.type === 'NETWORK_ERROR' || this.type === 'TIMEOUT';
  }

  /**
   * 인증 관련 에러인지 확인
   */
  get isAuthError(): boolean {
    return this.type === 'UNAUTHORIZED' || this.type === 'FORBIDDEN';
  }

  /**
   * 클라이언트 에러인지 확인 (4xx)
   */
  get isClientError(): boolean {
    return this.statusCode >= 400 && this.statusCode < 500;
  }

  /**
   * 서버 에러인지 확인 (5xx)
   */
  get isServerError(): boolean {
    return this.statusCode >= 500;
  }

  /**
   * Axios 에러에서 ApiError 생성
   */
  static fromAxiosError(error: AxiosError<ApiErrorResponse>): ApiError {
    // 1. 네트워크 에러 (응답이 없는 경우)
    if (!error.response) {
      return ApiError.fromNetworkError(error);
    }

    // 2. HTTP 응답이 있는 경우
    const { status, data } = error.response;
    const backendMessage = data?.message || data?.error?.message;
    const backendCode = data?.error?.code;
    const details = data?.error?.details;

    const { type, userMessage } = ApiError.classifyHttpStatus(status, backendMessage);

    return new ApiError({
      type,
      statusCode: status,
      message: backendMessage || error.message,
      userMessage,
      backendCode,
      details,
    });
  }

  /**
   * 네트워크 에러 생성
   */
  private static fromNetworkError(error: AxiosError): ApiError {
    const errorCode = (error as unknown as { code?: string }).code || '';
    const message = error.message || '';

    // 타임아웃
    if (errorCode === 'ECONNABORTED' || message.toLowerCase().includes('timeout')) {
      return new ApiError({
        type: 'TIMEOUT',
        statusCode: 0,
        message: 'Request timeout',
        userMessage: '요청 시간이 초과되었습니다. 네트워크 연결을 확인해주세요.',
      });
    }

    // 연결 거부/실패
    if (
      errorCode === 'ECONNREFUSED' ||
      errorCode === 'ENOTFOUND' ||
      errorCode === 'ECONNRESET' ||
      message.includes('Network Error')
    ) {
      return new ApiError({
        type: 'NETWORK_ERROR',
        statusCode: 0,
        message: 'Network connection failed',
        userMessage: '서버에 연결할 수 없습니다. 네트워크 연결을 확인해주세요.',
      });
    }

    // 기타 네트워크 에러
    return new ApiError({
      type: 'NETWORK_ERROR',
      statusCode: 0,
      message: message || 'Unknown network error',
      userMessage: '네트워크 연결을 확인해주세요.',
    });
  }

  /**
   * HTTP 상태 코드 분류
   */
  private static classifyHttpStatus(
    status: number,
    backendMessage?: string
  ): { type: ErrorType; userMessage: string } {
    const statusMap: Record<number, { type: ErrorType; userMessage: string }> = {
      400: { type: 'BAD_REQUEST', userMessage: '입력 정보가 올바르지 않습니다.' },
      401: { type: 'UNAUTHORIZED', userMessage: '인증이 필요합니다. 다시 로그인해주세요.' },
      403: { type: 'FORBIDDEN', userMessage: '접근 권한이 없습니다.' },
      404: { type: 'NOT_FOUND', userMessage: '요청한 리소스를 찾을 수 없습니다.' },
      409: { type: 'CONFLICT', userMessage: '이미 존재하는 리소스입니다.' },
      422: { type: 'VALIDATION_ERROR', userMessage: '입력 데이터가 올바르지 않습니다.' },
      429: { type: 'TOO_MANY_REQUESTS', userMessage: '너무 많은 요청입니다. 잠시 후 다시 시도해주세요.' },
      500: { type: 'SERVER_ERROR', userMessage: '서버 오류가 발생했습니다.' },
      502: { type: 'SERVER_ERROR', userMessage: '서버에 연결할 수 없습니다.' },
      503: { type: 'SERVER_ERROR', userMessage: '서비스가 일시적으로 이용 불가능합니다.' },
      504: { type: 'TIMEOUT', userMessage: '서버 응답 시간이 초과되었습니다.' },
    };

    const defaultError = statusMap[status] || {
      type: 'UNKNOWN' as ErrorType,
      userMessage: '알 수 없는 오류가 발생했습니다.',
    };

    // 백엔드 메시지가 있으면 우선 사용
    return {
      type: defaultError.type,
      userMessage: backendMessage || defaultError.userMessage,
    };
  }

  /**
   * 알 수 없는 에러에서 ApiError 생성
   */
  static fromUnknown(error: unknown): ApiError {
    // 이미 ApiError인 경우
    if (error instanceof ApiError) {
      return error;
    }

    // Axios 에러인 경우
    if (isAxiosError(error)) {
      return ApiError.fromAxiosError(error as AxiosError<ApiErrorResponse>);
    }

    // 일반 Error인 경우
    if (error instanceof Error) {
      return new ApiError({
        type: 'UNKNOWN',
        statusCode: 0,
        message: error.message,
        userMessage: '오류가 발생했습니다.',
      });
    }

    // 기타
    return new ApiError({
      type: 'UNKNOWN',
      statusCode: 0,
      message: String(error),
      userMessage: '알 수 없는 오류가 발생했습니다.',
    });
  }
}
